import React, { useState, useEffect, useRef } from "react";
import { getBotDashboard } from "../../services/api";
import "../Query/QueriesLog.css";

// Interface mapped exactly to the lead API response item
interface LeadRecord {
  id: string;
  sessionToken: string;
  name: string;
  email: string | null;
  createdAt: string; // ISO date string e.g., "2026-07-29T09:04:07.148Z"
  updatedAt: string;
}

export const LeadLog: React.FC = () => {
  // Date states in YYYY-MM-DD
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7); // Default to last 7 days
    return d.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });

  // Calendar visibility & focus
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectingField, setSelectingField] = useState<"from" | "to">("from"); // Active underline segment
  const [currentMonthLeft, setCurrentMonthLeft] = useState(() => {
    return new Date(2026, 6, 1); // July 2026
  });
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const datepickerRef = useRef<HTMLDivElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [records, setRecords] = useState<LeadRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Column level search states
  const [nameSearch, setNameSearch] = useState("");
  const [showNameSearch, setShowNameSearch] = useState(false);
  const [tempNameSearch, setTempNameSearch] = useState("");
  const nameSearchRef = useRef<HTMLDivElement>(null);

  const [emailSearch, setEmailSearch] = useState("");
  const [showEmailSearch, setShowEmailSearch] = useState(false);
  const [tempEmailSearch, setTempEmailSearch] = useState("");
  const emailSearchRef = useRef<HTMLDivElement>(null);

  const handleToggleNameSearch = () => {
    if (!showNameSearch) {
      setTempNameSearch(nameSearch);
    }
    setShowNameSearch(!showNameSearch);
  };

  const handleToggleEmailSearch = () => {
    if (!showEmailSearch) {
      setTempEmailSearch(emailSearch);
    }
    setShowEmailSearch(!showEmailSearch);
  };

  const handleNameSearchSubmit = () => {
    setNameSearch(tempNameSearch);
    setShowNameSearch(false);
  };

  const handleEmailSearchSubmit = () => {
    setEmailSearch(tempEmailSearch);
    setShowEmailSearch(false);
  };

  const handleNameSearchReset = () => {
    setTempNameSearch("");
    setNameSearch("");
    setShowNameSearch(false);
  };

  const handleEmailSearchReset = () => {
    setTempEmailSearch("");
    setEmailSearch("");
    setShowEmailSearch(false);
  };

  // State to track if the table has been scrolled horizontally
  const [scrolled, setScrolled] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Helper to trigger toast notification
  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  // Close calendar picker and search dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      if (datepickerRef.current && !datepickerRef.current.contains(targetNode)) {
        setIsPickerOpen(false);
      }
      if (nameSearchRef.current && !nameSearchRef.current.contains(targetNode)) {
        setShowNameSearch(false);
      }
      if (emailSearchRef.current && !emailSearchRef.current.contains(targetNode)) {
        setShowEmailSearch(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch data from API
  const fetchLeads = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await getBotDashboard(fromDate, toDate, "lead");
      if (response && Array.isArray(response.data)) {
        setRecords(response.data);
      } else if (Array.isArray(response)) {
        setRecords(response);
      } else {
        setRecords([]);
      }
      
      if (isRefresh) {
        triggerToast("Leads log refreshed successfully!");
      }
    } catch (err: any) {
      console.error("Error fetching leads log:", err);
      setError("Failed to fetch lead logs. Please check connection.");
      triggerToast("Error fetching lead logs");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch when dates change
  useEffect(() => {
    fetchLeads();
  }, [fromDate, toDate]);

  // Reset to page 1 when filters or search inputs change
  useEffect(() => {
    setCurrentPage(1);
  }, [nameSearch, emailSearch, fromDate, toDate]);

  const handleRefresh = () => {
    fetchLeads(true);
  };

  const handleExport = () => {
    if (filteredRecords.length === 0) {
      triggerToast("No lead logs to export.");
      return;
    }

    const escapeCSV = (val: any) => {
      if (val === null || val === undefined) return "";
      const stringVal = String(val).trim();
      return `"${stringVal.replace(/"/g, '""')}"`;
    };

    const headers = [
      "S. No.",
      "Name",
      "Email",
      "Session Token",
      "Date and time (IST)"
    ];

    const rows = filteredRecords.map((record, index) => [
      index + 1,
      record.name || "",
      record.email || "",
      record.sessionToken || "",
      formatDateTime(record.createdAt)
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map(escapeCSV).join(","))
    ].join("\n");

    try {
      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      const dateStr = new Date().toISOString().split("T")[0];
      link.setAttribute("download", `leads_log_${dateStr}.csv`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      triggerToast("Leads log exported to CSV!");
    } catch (err) {
      console.error("CSV Export Error:", err);
      triggerToast("Failed to export leads log.");
    }
  };

  // Helper to format Date into YYYY-MM-DD
  const formatDateToString = (date: Date): string => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // Helper to format ISO strings into readable "DD/MM/YYYY hh:mm AM/PM"
  const formatDateTime = (isoString: string): string => {
    if (!isoString) return "";
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return isoString;

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12; // hour '0' should be '12'

      return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
    } catch {
      return isoString;
    }
  };

  // Format YYYY-MM-DD to YYYY/MM/DD for header trigger box matching screenshot
  const formatDisplayDate = (dateStr: string): string => {
    if (!dateStr) return "";
    return dateStr.replace(/-/g, "/");
  };

  // Scroll handler to toggle sticky drop-shadow highlight dynamically
  const handleTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    setScrolled(el.scrollLeft > 0);

    // Show scrollbar on scroll — hide it again 600ms after scrolling stops
    el.classList.add("is-scrolling");
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      el.classList.remove("is-scrolling");
    }, 600);
  };

  // Filter records locally by column searches
  const filteredRecords = records.filter((record) => {
    if (nameSearch && !record.name?.toLowerCase().includes(nameSearch.toLowerCase())) {
      return false;
    }
    if (emailSearch && !record.email?.toLowerCase().includes(emailSearch.toLowerCase())) {
      return false;
    }
    return true;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredRecords.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedRecords = filteredRecords.slice(startIndex, startIndex + pageSize);

  // Dual-Calendar calculations
  const leftMonthName = currentMonthLeft.toLocaleString("default", { month: "short" });
  const leftYear = currentMonthLeft.getFullYear();

  const rightMonthDate = new Date(currentMonthLeft.getFullYear(), currentMonthLeft.getMonth() + 1, 1);
  const rightMonthName = rightMonthDate.toLocaleString("default", { month: "short" });
  const rightYear = rightMonthDate.getFullYear();

  const changeMonth = (offset: number) => {
    setCurrentMonthLeft(new Date(currentMonthLeft.getFullYear(), currentMonthLeft.getMonth() + offset, 1));
  };

  const changeYear = (offset: number) => {
    setCurrentMonthLeft(new Date(currentMonthLeft.getFullYear() + offset, currentMonthLeft.getMonth(), 1));
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay(); // 0 = Sun, 6 = Sat
  };

  // Generate 42 calendar grid cells
  const generateMonthDays = (baseDate: Date) => {
    const year = baseDate.getFullYear();
    const month = baseDate.getMonth();
    
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const cells = [];
    
    // Prev month padding days
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);
    for (let i = firstDay - 1; i >= 0; i--) {
      cells.push({
        date: new Date(prevYear, prevMonth, daysInPrevMonth - i),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      cells.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month padding days
    const remaining = 42 - cells.length;
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    for (let i = 1; i <= remaining; i++) {
      cells.push({
        date: new Date(nextYear, nextMonth, i),
        isCurrentMonth: false
      });
    }
    
    return cells;
  };

  // Cell highlighting classes
  const getDayClass = (dayDate: Date, isCurrentMonth: boolean) => {
    let classes = "calendar-day-cell";
    if (!isCurrentMonth) {
      classes += " out-of-month";
      return classes;
    }

    const dateStr = formatDateToString(dayDate);
    const fromTime = new Date(fromDate).getTime();
    const toTime = new Date(toDate).getTime();
    const cellTime = dayDate.getTime();

    if (dateStr === fromDate) {
      classes += " is-start-date";
    } else if (dateStr === toDate) {
      classes += " is-end-date";
    } else if (cellTime > fromTime && cellTime < toTime) {
      classes += " is-in-range";
    } else if (
      selectingField === "to" &&
      hoverDate &&
      cellTime > fromTime &&
      cellTime <= hoverDate.getTime()
    ) {
      classes += " is-hover-range";
    }

    return classes;
  };

  // Day click selection logic
  const handleDateClick = (date: Date) => {
    const dateStr = formatDateToString(date);
    if (selectingField === "from") {
      setFromDate(dateStr);
      setSelectingField("to"); // auto switch to end date
    } else {
      const from = new Date(fromDate);
      if (date < from) {
        setFromDate(dateStr);
        setToDate(fromDate);
      } else {
        setToDate(dateStr);
      }
      setSelectingField("from"); // reset active segment
      setIsPickerOpen(false); // auto-close on selection completion
    }
  };

  // Quick Preset Handlers
  const handlePresetSelect = (preset: string) => {
    const today = new Date();
    let start = new Date();
    let end = new Date();

    switch (preset) {
      case "today":
        start = today;
        end = today;
        break;
      case "yesterday":
        start = new Date();
        start.setDate(today.getDate() - 1);
        end = new Date();
        end.setDate(today.getDate() - 1);
        break;
      case "last7":
        start = new Date();
        start.setDate(today.getDate() - 7);
        end = today;
        break;
      case "last30":
        start = new Date();
        start.setDate(today.getDate() - 30);
        end = today;
        break;
      case "thisMonth":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case "lastMonth":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
    }

    setFromDate(formatDateToString(start));
    setToDate(formatDateToString(end));
    setSelectingField("from");
    setIsPickerOpen(false);
  };

  const leftDays = generateMonthDays(currentMonthLeft);
  const rightDays = generateMonthDays(rightMonthDate);
  const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  return (
    <div className="queries-log-container">
      {/* Date selector and Controls header card */}
      <div className="queries-header-card">
        <div className="queries-datepicker-container" ref={datepickerRef}>
          <span className="datepicker-label">
            <i className="fa-regular fa-calendar-days" /> Select Date Range:
          </span>
          <div className="queries-datepicker-wrapper" id="queries-datepicker-wrapper-id">
            {/* Input Trigger Box */}
            <div
              className={`queries-datepicker-trigger ${isPickerOpen ? "active" : ""}`}
              onClick={() => setIsPickerOpen(!isPickerOpen)}
            >
              <div
                className={`date-field-val ${selectingField === "from" ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectingField("from");
                  setIsPickerOpen(true);
                }}
              >
                {formatDisplayDate(fromDate)}
              </div>
              <span className="date-separator">➔</span>
              <div
                className={`date-field-val ${selectingField === "to" ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectingField("to");
                  setIsPickerOpen(true);
                }}
              >
                {formatDisplayDate(toDate)}
              </div>
              <i className="fa-regular fa-calendar date-calendar-icon" />
            </div>

            {/* Calendar Dropdown Dropdown */}
            {isPickerOpen && (
              <div className="calendar-dropdown-card">
                {/* Year/Month Selection Navigation */}
                <div className="calendar-header">
                  <div className="header-nav">
                    <button className="nav-btn" onClick={() => changeYear(-1)}>«</button>
                    <button className="nav-btn" onClick={() => changeMonth(-1)}>‹</button>
                  </div>
                  <div className="header-months-title">
                    <span className="month-title">{leftMonthName} {leftYear}</span>
                    <span className="month-title">{rightMonthName} {rightYear}</span>
                  </div>
                  <div className="header-nav">
                    <button className="nav-btn" onClick={() => changeMonth(1)}>›</button>
                    <button className="nav-btn" onClick={() => changeYear(1)}>»</button>
                  </div>
                </div>

                {/* Grids Display */}
                <div className="calendar-grids-container">
                  {/* Left Calendar Grid */}
                  <div className="calendar-grid-month">
                    <div className="week-headers">
                      {weekDays.map((d) => (
                        <span key={d} className="week-header-day">{d}</span>
                      ))}
                    </div>
                    <div className="days-grid">
                      {leftDays.map((cell, i) => (
                        <div
                          key={`left-${i}`}
                          className={getDayClass(cell.date, cell.isCurrentMonth)}
                          onMouseEnter={() => setHoverDate(cell.date)}
                          onClick={() => handleDateClick(cell.date)}
                        >
                          {cell.date.getDate()}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right Calendar Grid */}
                  <div className="calendar-grid-month">
                    <div className="week-headers">
                      {weekDays.map((d) => (
                        <span key={d} className="week-header-day">{d}</span>
                      ))}
                    </div>
                    <div className="days-grid">
                      {rightDays.map((cell, i) => (
                        <div
                          key={`right-${i}`}
                          className={getDayClass(cell.date, cell.isCurrentMonth)}
                          onMouseEnter={() => setHoverDate(cell.date)}
                          onClick={() => handleDateClick(cell.date)}
                        >
                          {cell.date.getDate()}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Presets List */}
                <div className="calendar-presets-container">
                  <button className="preset-btn" onClick={() => handlePresetSelect("today")}>Today</button>
                  <button className="preset-btn" onClick={() => handlePresetSelect("yesterday")}>Yesterday</button>
                  <button className="preset-btn" onClick={() => handlePresetSelect("last7")}>Last 7 Days</button>
                  <button className="preset-btn" onClick={() => handlePresetSelect("last30")}>Last 30 Days</button>
                  <button className="preset-btn" onClick={() => handlePresetSelect("thisMonth")}>This Month</button>
                  <button className="preset-btn" onClick={() => handlePresetSelect("lastMonth")}>Last Month</button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="queries-actions-container">
          <button className="queries-btn queries-refresh-btn" onClick={handleRefresh} disabled={loading || refreshing}>
            <i className={`fa-solid fa-rotate ${refreshing ? "fa-spin" : ""}`} />
            Refresh
          </button>

          <button className="queries-btn queries-export-btn" onClick={handleExport}>
            <i className="fa-solid fa-download" />
            Export
          </button>
        </div>
      </div>

      {/* Leads Table Card */}
      <div className="queries-table-card">
        <div className={`queries-table-wrapper ${scrolled ? "is-scrolled" : ""}`} onScroll={handleTableScroll}>
          <table className="queries-table">
            <thead>
              <tr>
                <th className="sno-col">S. No.</th>
                <th className="query-col">
                  <div className="header-content-wrapper" ref={nameSearchRef}>
                    <span>Name</span>
                    <i
                      className={`fa-solid fa-magnifying-glass column-search-trigger ${
                        showNameSearch || nameSearch ? "active" : ""
                      }`}
                      onClick={handleToggleNameSearch}
                    />
                    {showNameSearch && (
                      <div className="column-search-dropdown-card">
                        <input
                          type="text"
                          className="column-search-dropdown-input"
                          placeholder="Search name"
                          value={tempNameSearch}
                          onChange={(e) => setTempNameSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleNameSearchSubmit();
                            } else if (e.key === "Escape") {
                              setShowNameSearch(false);
                            }
                          }}
                          autoFocus
                        />
                        <div className="column-search-dropdown-actions">
                          <button
                            type="button"
                            className="column-search-dropdown-btn-search"
                            onClick={handleNameSearchSubmit}
                          >
                            <i className="fa-solid fa-magnifying-glass btn-icon" />
                            Search
                          </button>
                          <button
                            type="button"
                            className="column-search-dropdown-btn-reset"
                            onClick={handleNameSearchReset}
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </th>
                <th className="answer-col">
                  <div className="header-content-wrapper" ref={emailSearchRef}>
                    <span>Email</span>
                    <i
                      className={`fa-solid fa-magnifying-glass column-search-trigger ${
                        showEmailSearch || emailSearch ? "active" : ""
                      }`}
                      onClick={handleToggleEmailSearch}
                    />
                    {showEmailSearch && (
                      <div className="column-search-dropdown-card">
                        <input
                          type="text"
                          className="column-search-dropdown-input"
                          placeholder="Search email"
                          value={tempEmailSearch}
                          onChange={(e) => setTempEmailSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleEmailSearchSubmit();
                            } else if (e.key === "Escape") {
                              setShowEmailSearch(false);
                            }
                          }}
                          autoFocus
                        />
                        <div className="column-search-dropdown-actions">
                          <button
                            type="button"
                            className="column-search-dropdown-btn-search"
                            onClick={handleEmailSearchSubmit}
                          >
                            <i className="fa-solid fa-magnifying-glass btn-icon" />
                            Search
                          </button>
                          <button
                            type="button"
                            className="column-search-dropdown-btn-reset"
                            onClick={handleEmailSearchReset}
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </th>
                <th className="uuid-col">Session Token</th>
                <th className="date-col">Date and time (IST)</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRecords.length > 0 &&
                paginatedRecords.map((record, index) => {
                  return (
                    <tr key={record.id}>
                      <td className="sno-col">{startIndex + index + 1}</td>
                      <td className="query-col">{record.name}</td>
                      <td className="answer-col">{record.email || "N/A"}</td>
                      <td className="uuid-col" style={{ fontFamily: "monospace" }}>
                        {record.sessionToken || "N/A"}
                      </td>
                      <td className="date-col">{formatDateTime(record.createdAt)}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Empty, Loading, and Error States rendered outside scroll wrapper for perfect centering */}
        {loading ? (
          <div className="table-empty-state">
            <i className="fa-solid fa-spinner fa-spin table-empty-icon" />
            <p>Loading lead logs...</p>
          </div>
        ) : error ? (
          <div className="table-empty-state">
            <i className="fa-solid fa-circle-exclamation table-empty-icon" style={{ color: "#dc2626" }} />
            <p style={{ color: "#dc2626" }}>{error}</p>
          </div>
        ) : paginatedRecords.length === 0 ? (
          <div className="table-empty-state">
            <i className="fa-solid fa-folder-open table-empty-icon" />
            <p>No leads found matching the selected filters.</p>
          </div>
        ) : null}

        {/* Pagination Controls */}
        {filteredRecords.length > 0 && (
          <div className="queries-pagination-container">
            <div className="pagination-pages">
              <button
                className="page-nav-btn"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`page-number-btn ${currentPage === page ? "active" : ""}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              <button
                className="page-nav-btn"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
            </div>

            <div className="pagination-stats">
              <div className="page-size-selector">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                </select>
              </div>
              <span className="total-queries-text">Total {filteredRecords.length} Leads</span>
            </div>
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div className="queries-toast">
          <i className="fa-solid fa-circle-check queries-toast-icon" />
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
};
